import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import seoByPath from "../../seo/generated.json";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.imeneyahiaoui.com";
const DEFAULT_IMAGE = `${SITE_URL}/assets/images/logo-2026.webp`;

const person = {
  "@type": "Person",
  "@id": `${SITE_URL}/#imene-yahiaoui`,
  name: "Imene Yahiaoui",
  url: `${SITE_URL}/`,
  image: DEFAULT_IMAGE,
  jobTitle: "Développeuse Full Stack freelance React, Angular, Node.js et NestJS",
  email: "ikhlefyahiaoui.imene@gmail.com",
  telephone: "+33612218389",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Paris",
    addressRegion: "Île-de-France",
    addressCountry: "FR",
  },
  knowsAbout: [
    "React",
    "Angular",
    "Node.js",
    "NestJS",
    "SaaS",
    "PWA",
    "SEO technique",
    "Core Web Vitals",
    "Performance web",
    "Accessibilité web",
  ],
  sameAs: [
    "https://www.linkedin.com/in/imene-yahiaoui/",
    "https://github.com/imene-yahiaoui",
  ],
};

export const getRouteSeo = (pathname) => seoByPath[pathname] || {
  title: "Page non trouvée | Imene Yahiaoui",
  description: "Cette page est introuvable. Retrouvez les services, projets et coordonnées d'Imene Yahiaoui.",
  noindex: true,
};

const setMeta = (selector, attributeName, attributeValue, content) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const setCanonical = (url) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
};

const setJsonLd = (schema) => {
  let script = document.head.querySelector('script[data-seo-json-ld="main"]');

  if (!script) {
    script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-seo-json-ld", "main");
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
};

const getBreadcrumb = (canonicalUrl, title, normalizedPath) => ({
  "@type": "BreadcrumbList",
  "@id": `${canonicalUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Accueil",
      item: `${SITE_URL}/`,
    },
    ...(normalizedPath === "/"
      ? []
      : [
          {
            "@type": "ListItem",
            position: 2,
            name: title.replace(" | Imene Yahiaoui", ""),
            item: canonicalUrl,
          },
        ]),
  ],
});

const getStructuredData = (seo, canonicalUrl, normalizedPath, language) => {
  const pageId = `${canonicalUrl}#webpage`;
  const isProfilePage = seo.pageType === "ProfilePage";
  const service = seo.serviceName
    ? {
        "@type": "Service",
        "@id": `${canonicalUrl}#service`,
        name: seo.serviceName,
        provider: {
          "@id": person["@id"],
        },
        areaServed: [
          { "@type": "AdministrativeArea", name: "Île-de-France" },
          { "@type": "Country", name: "France" },
        ],
        serviceType: seo.serviceName,
        url: canonicalUrl,
      }
    : null;

  return {
    "@context": "https://schema.org",
    "@graph": [
      person,
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: "Imene Yahiaoui",
        inLanguage: language,
        publisher: {
          "@id": person["@id"],
        },
      },
      {
        "@type": seo.pageType || "WebPage",
        "@id": pageId,
        url: canonicalUrl,
        name: seo.title,
        description: seo.description,
        inLanguage: language,
        isPartOf: {
          "@id": `${SITE_URL}/#website`,
        },
        about: {
          "@id": person["@id"],
        },
        ...(service ? { mainEntity: { "@id": service["@id"] } } : {}),
        ...(isProfilePage ? { mainEntity: { "@id": person["@id"] } } : {}),
        breadcrumb: {
          "@id": `${canonicalUrl}#breadcrumb`,
        },
      },
      getBreadcrumb(canonicalUrl, seo.title, normalizedPath),
      ...(service ? [service] : []),
      ...(seo.article ? [{
        "@type": "BlogPosting", "@id": canonicalUrl + "#article",
        headline: seo.title.replace(" | Imene Yahiaoui", ""),
        description: seo.description, image: seo.image,
        author: { "@id": person["@id"] },
        mainEntityOfPage: { "@id": pageId }, inLanguage: language,
      }] : []),
    ],
  };
};

const SeoManager = () => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const normalizedPath = pathname.replace(/\/+$/, "") || "/";
    const canonicalUrl = `${SITE_URL}${normalizedPath === "/" ? "/" : normalizedPath}`;
    const base = getRouteSeo(normalizedPath);
    const selectedLanguage = (i18n.resolvedLanguage || "fr").split("-")[0];
    const seo = { ...base, ...(base.translations?.[selectedLanguage] || {}) };
    const language = base.serviceName && !["/services", "/audit"].includes(normalizedPath) ? "fr" : selectedLanguage;
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";

    document.title = seo.title;
    setCanonical(canonicalUrl);
    setMeta('meta[name="robots"]', "name", "robots", seo.noindex ? "noindex, follow" : "index, follow, max-image-preview:large");
    setMeta('meta[name="description"]', "name", "description", seo.description);
    setMeta('meta[property="og:title"]', "property", "og:title", seo.title);
    setMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      seo.description,
    );
    setMeta('meta[property="og:type"]', "property", "og:type", seo.article ? "article" : "website");
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    setMeta('meta[property="og:image"]', "property", "og:image", seo.image || DEFAULT_IMAGE);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", seo.title);
    setMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      seo.description,
    );
    setMeta('meta[name="twitter:url"]', "name", "twitter:url", canonicalUrl);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", seo.image || DEFAULT_IMAGE);
    setJsonLd(getStructuredData(seo, canonicalUrl, normalizedPath, language));
      document.documentElement.dataset.seoPath = normalizedPath;
  }, [pathname, i18n.resolvedLanguage]);

  return null;
};

export default SeoManager;
